import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import {
  AsyncPipe,
  DatePipe,
  KeyValuePipe,
  TitleCasePipe,
} from '@angular/common';

import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';

import { TranslateModule } from '@ngx-translate/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

import { SearchLinkModule } from 'src/app/shared/search-link/search-link.module';
import { LocationListModule } from 'src/app/shared/location-list/location-list.module';

import { SearchInputFieldComponent } from 'src/app/shared/components/forms/search-input-field/search-input-field.component';

import { ReplaceStrPipe } from 'src/app/shared/pipes/replace-str.pipe';
import { ParseCoordinatesPipe } from 'src/app/shared/pipes/parse-coordinates.pipe';
import { ExtractFirstParagraphPipe } from 'src/app/shared/pipes/extract-first-paragraph.pipe';

import { AuthService } from 'src/app/core/services/auth.service';
import { ScrollToIdService } from 'src/app/core/services/scroll-to-id.service';

import { CountryInfoTextService } from '../../../services/country-info-text.service';
import { MetaTagsConfigService } from 'src/app/core/services/meta-tags-config.service';
import { HotelsProviderServiceService } from 'src/app/shared/used-components/hotels-booking-container/hotels-provider-service.service';

import { CityDetails } from 'src/app/core/types/models/city-details.model';
import { CityResponse } from 'src/app/core/types/models/city-response.model';
import { RegionDetails } from 'src/app/core/types/models/region-details.model';
import { VoiceMapResponse } from 'src/app/core/types/models/voice-map-response';
import { RegionResponse } from 'src/app/core/types/models/region-response.model';
import { OccasionGeniusResponse } from 'src/app/core/types/models/occasion-genius-response.model';

import { PageInfoComponent } from '../../../pages/page-info';
import { GalleryComponent } from 'src/app/shared/components/gallery/gallery.component';
import { SimpleMapComponent } from 'src/app/shared/maps/simple-map/simple-map.component';
import { TripsListComponent } from '../../../components/trips-list/trips-list.component';
import { PlacesListComponent } from '../../../components/places-list/places-list.component';
import { AudioToursComponent } from 'src/app/shared/used-components/audio-tours/audio-tours.component';
import { TextEllipsisComponent } from 'src/app/shared/components/text-ellipsis/text-ellipsis.component';
import { BookingBannerComponent } from 'src/app/shared/components/booking-banner/booking-banner.component';
import { OccasionGeniusComponent } from 'src/app/shared/components/occasion-genius/occasion-genius.component';
import { SimpleBreadcrumbComponent } from '../../../components/simple-breadcrumb/simple-breadcrumb.component';
import { PopularItinerariesComponent } from 'src/app/shared/components/popular-itineraries/popular-itineraries.component';
import {
  IViatorCategories,
  ViatorComponent,
} from 'src/app/shared/used-components/viator/viator.component';
import {
  HOTEL_CLIENT,
  HotelsBookingContainerComponent,
} from 'src/app/shared/used-components/hotels-booking-container/hotels-booking-container.component';

@Component({
  selector: 'app-city-info-page-desktop',
  templateUrl: './city-info-page-desktop.component.html',
  styleUrls: ['./city-info-page-desktop.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    LocationListModule,
    MatIconModule,
    MatMenuModule,
    SearchLinkModule,
    TranslateModule,
    NgxSkeletonLoaderModule,
    ParseCoordinatesPipe,
    SimpleMapComponent,
    ExtractFirstParagraphPipe,
    ReplaceStrPipe,
    RouterLink,
    TitleCasePipe,
    KeyValuePipe,
    ViatorComponent,
    GalleryComponent,
    TripsListComponent,
    AudioToursComponent,
    PlacesListComponent,
    TextEllipsisComponent,
    BookingBannerComponent,
    OccasionGeniusComponent,
    SearchInputFieldComponent,
    SimpleBreadcrumbComponent,
    PopularItinerariesComponent,
    HotelsBookingContainerComponent,
  ],
  providers: [
    DatePipe,
    CountryInfoTextService,
    {
      provide: HOTEL_CLIENT,
      useClass: HotelsProviderServiceService,
    },
  ],
})
export class CityInfoPageDesktopComponent
  extends PageInfoComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input({ required: true }) cityResponse: CityResponse = new CityResponse();
  @Input({ required: true }) cityDetails: CityDetails = new CityDetails();

  @Input({ required: true }) regionResponse: RegionResponse =
    new RegionResponse();
  @Input({ required: true }) regionDetails: RegionDetails = new RegionDetails();

  @Input({ required: true }) categoriesResponse: IViatorCategories = {};
  @Input({ required: true }) occasionGeniusResponse: OccasionGeniusResponse =
    new OccasionGeniusResponse({});
  @Input({ required: true }) voiceMapResponse: VoiceMapResponse =
    new VoiceMapResponse();

  public menuOpened: boolean[] = [];
  public numOfHurt: number = 0;

  constructor(
    protected cdr: ChangeDetectorRef,
    protected scrollToIdService: ScrollToIdService,
    protected countryInfoTextService: CountryInfoTextService,
    protected authService: AuthService,
    private readonly metaTagsConfigService: MetaTagsConfigService,
  ) {
    super(cdr, scrollToIdService);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.cityResponse && changes.cityResponse.currentValue) {
      this.setCityPageSeoTags();
    }
  }

  ngOnInit() {
    this.setCityPageSeoTags();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  private setCityPageSeoTags(): void {
    if (this.cityDetails.name) {
      this.metaTagsConfigService.setCitySeoTags(
        this.cityDetails.name,
        this.regionDetails.name,
        this.countryDetails.name,
      );
    }
  }
}
